import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { BrandLogo } from "../lib/siteConfig";
import { useTheme } from "../lib/theme";
import { LogOut, LayoutDashboard, MapPin, FileText, Wrench, CalendarDays, Users, Building2, ClipboardList, Wheat, Repeat, Inbox, Settings, Sun, Moon, CalendarClock, IndianRupee, ListChecks } from "lucide-react";

function ProjectBadge() {
  const raw = typeof window !== "undefined" ? localStorage.getItem("activeProject") : null;
  if (!raw) return null;
  try {
    const p = JSON.parse(raw);
    return (
      <div className="text-[10px] uppercase tracking-[0.15em] text-primary mt-0.5 font-semibold" data-testid="active-project-badge">{p.name}</div>
    );
  } catch { return null; }
}

const NAV = {
  customer: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/plots", label: "My Plots", icon: MapPin },
    { to: "/app/documents", label: "Documents", icon: FileText },
    { to: "/app/yields", label: "Yields", icon: Wheat },
    { to: "/app/bookings", label: "Cottage", icon: CalendarDays },
  ],
  field_worker: [
    { to: "/app", label: "My Tasks", icon: ClipboardList, end: true },
    { to: "/app/log-activity", label: "Log Activity", icon: MapPin },
  ],
  property_manager: [
    { to: "/app", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/app/projects", label: "Projects", icon: Building2 },
    { to: "/app/plots", label: "Plots", icon: MapPin },
    { to: "/app/maintenance", label: "Maintenance", icon: Wrench },
    { to: "/app/recurring", label: "Recurring", icon: Repeat },
    { to: "/app/rate-cards", label: "Rate cards", icon: CalendarClock },
    { to: "/app/payments", label: "Payments", icon: IndianRupee },
    { to: "/app/bookings", label: "Bookings", icon: CalendarDays },
    { to: "/app/documents", label: "Documents", icon: FileText },
    { to: "/app/yields", label: "Yields", icon: Wheat },
    { to: "/app/enquiries", label: "Enquiries", icon: Inbox },
  ],
  super_admin: [
    { to: "/app", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/app/projects", label: "Projects", icon: Building2 },
    { to: "/app/plots", label: "Plots", icon: MapPin },
    { to: "/app/maintenance", label: "Maintenance", icon: Wrench },
    { to: "/app/recurring", label: "Recurring", icon: Repeat },
    { to: "/app/rate-cards", label: "Rate cards", icon: CalendarClock },
    { to: "/app/payments", label: "Payments", icon: IndianRupee },
    { to: "/app/bookings", label: "Bookings", icon: CalendarDays },
    { to: "/app/documents", label: "Documents", icon: FileText },
    { to: "/app/yields", label: "Yields", icon: Wheat },
    { to: "/app/enquiries", label: "Enquiries", icon: Inbox },
    { to: "/app/users", label: "Users", icon: Users },
    { to: "/app/field-definitions", label: "Custom fields", icon: ListChecks },
    { to: "/app/settings", label: "Settings", icon: Settings },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { theme, toggle } = useTheme();
  const items = NAV[user?.role] || [];

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-border bg-card">
        <div className="p-6 border-b border-border">
          <Link to="/app" className="flex items-center gap-2">
            <BrandLogo size={36} />
            <div>
            <div className="heading text-sm font-semibold leading-tight">Roots by Sanjeevani Farms</div>
            <ProjectBadge />
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-[background-color,color] duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <it.icon size={16} />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] mt-1 text-muted-foreground">{user?.role?.replace("_", " ")}</div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" className="flex-1" onClick={onLogout} data-testid="logout-btn">
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
            <Button variant="outline" size="sm" onClick={toggle} data-testid="theme-toggle-btn" aria-label="Toggle theme">
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            </Button>
          </div>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-card border-b border-border px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BrandLogo size={32} />
          <span className="heading font-semibold text-sm">Roots by Sanjeevani Farms</span>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={toggle} data-testid="theme-toggle-btn-mobile" aria-label="Toggle theme">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </Button>
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn-mobile"><LogOut size={16} /></Button>
        </div>
      </div>

      <main className="flex-1 md:ml-0 mt-14 md:mt-0 min-w-0">
        {children}
        {/* Mobile bottom nav */}
        <div className="md:hidden fixed bottom-0 inset-x-0 bg-card border-t border-border grid grid-cols-4 z-40">
          {items.slice(0, 4).map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}
              className={({ isActive }) => `flex flex-col items-center py-2 text-[10px] ${isActive ? "text-primary" : "text-muted-foreground"}`}
              data-testid={`mnav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <it.icon size={18} />
              <span className="mt-1">{it.label}</span>
            </NavLink>
          ))}
        </div>
      </main>
    </div>
  );
}
